import { useEffect, useMemo, useRef, useState } from "react"
import { router } from "@inertiajs/react"
import { Check, Feather } from "lucide-react"

import AppShell from "@/components/tuw/AppShell"
import { WizardShell } from "@/components/tuw/post/WizardShell"
import { PriceRail } from "@/components/tuw/post/PriceRail"
import { STEPPER_COLLAPSE } from "@/components/tuw/post/Stepper"
import { PostedConfirmation } from "@/components/tuw/post/PostedConfirmation"
import { StepFields } from "@/components/tuw/openHouse/StepFields"
import { ReviewStep } from "@/components/tuw/openHouse/ReviewStep"
import { useDraftSave } from "@/lib/post/useDraftSave"
import * as options from "@/lib/post/options"
import { createDraft, priceProject, submitProject } from "@/lib/postProjectActions"
import { BUNDLE_COMPOSITION, PLATINUM } from "@/lib/post/bundle"
import { markOpenHouse } from "@/lib/openHouse/actions"
import { isOutlineFile, issueTarget, reviewSections, stepIssues } from "@/lib/openHouse/bookWritingSteps"
import { isManuscriptFile } from "@/lib/openHouse/editingSteps"
import {
  acknowledgePostingFile, answerUpdate, createParams, draftPayload, initialState, openHousePayload, persist, postingFile,
  withoutUploaded,
} from "@/lib/openHouse/draft"
import { postingCtx, requestedService, serviceOf } from "@/lib/openHouse/services"
import { WORDING } from "@/lib/openHouse/wording"

// Open House — the new Post a Project for book writing, the paid sample,
// editing and bundles (spec open-house.md, decisions 9–17; minimums.md). It asks only what
// sets the price or what a freelancer needs to decide whether to request; the
// rest moves to the brief. The service comes from ?service= or the resumed draft.
//
// The draft is today's: created with POST /post/drafts on the first answer,
// marked Open House, autosaved through today's PATCH /post/drafts/:id (the
// service's answers, editing's manuscript file and the cursor) and PATCH
// /post/drafts/:id/open_house (short description, outline text, the
// supply-my-own choice), then posted with today's submit, where the server
// applies the Open House rules. Today's Post a Project (pages/PostProject.jsx)
// is untouched.

function focusField(nodes, key) {
  const node = nodes.get(key) || document.querySelector(`[data-post-field="${key}"]`)
  node?.scrollIntoView({ behavior: "smooth", block: "center" })
  node?.querySelector("input, textarea, button, [tabindex]:not([tabindex='-1'])")?.focus({ preventScroll: true })
}

export default function OpenHousePost({ catalog = {}, openHouse = null }) {
  const [state, setState] = useState(() => initialState(catalog, openHouse, requestedService(window.location.search)))
  const [showErr, setShowErr] = useState(false)
  const [serverErrors, setServerErrors] = useState({})
  const [reviewIssues, setReviewIssues] = useState([])
  const [terms, setTerms] = useState(false)
  const [postFailed, setPostFailed] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [posted, setPosted] = useState(null)
  const [startError, setStartError] = useState(null)
  const [upload, setUpload] = useState({ status: "idle", file: null })
  const [serverCost, setServerCost] = useState(null)
  const fieldNodes = useRef(new Map())
  const pendingFocus = useRef(null)
  const topRef = useRef(null)
  const stateRef = useRef(state)
  stateRef.current = state
  const creatingRef = useRef(null)
  const createdIdRef = useRef(null)
  const explicitSaveRef = useRef(false)
  const editedRef = useRef(false)
  const uploadedRef = useRef(new WeakSet())
  const removedRef = useRef(new Set())
  const removalRef = useRef(0)

  const ctx = useMemo(
    () => postingCtx(catalog, state.service, state.posting.bundle_tier),
    [catalog, state.service, state.posting.bundle_tier]
  )
  const service = serviceOf(state.service)
  const ctxRef = useRef(ctx)
  ctxRef.current = ctx

  const { savedAt, saveError, retryingSave, noteDraftEdit, saveCurrentDraft, retrySave } = useDraftSave({
    saveDraft: persist,
    getSnapshot: () => stateRef.current,
    getPayload: (snapshot) => ({
      draft: draftPayload(snapshot, ctxRef.current), openHouse: openHousePayload(snapshot.oh), removal: snapshot.oh.removeFile,
    }),
    preparePayload: (payload) => withoutUploaded(payload, uploadedRef.current, removedRef.current),
    onSaving: (_snapshot, payload) => {
      const file = payload.openHouse.outline_file || postingFile(payload.draft)
      if (file) setUpload({ status: "uploading", file })
    },
    onSaved: (snapshot, payload) => {
      const manuscript = postingFile(payload.draft)
      if (manuscript) {
        uploadedRef.current.add(manuscript)
        setUpload((prev) => (prev.file === manuscript ? { status: "done", file: null } : prev))
        setState((prev) => acknowledgePostingFile(prev, manuscript))
      }
      const { outline_file: file, remove_outline_file: removed } = payload.openHouse
      if (file) {
        uploadedRef.current.add(file)
        setUpload((prev) => (prev.file === file ? { status: "done", file: null } : prev))
      }
      if (removed) removedRef.current.add(payload.removal)
      if (!file && !removed) return
      setState((prev) => {
        const oh = { ...prev.oh }
        if (file && oh.pendingFile === file) Object.assign(oh, { pendingFile: null, outlineFile: { filename: file.name } })
        if (removed && oh.removeFile === snapshot.oh.removeFile) oh.removeFile = null
        return { ...prev, oh }
      })
    },
    onFailed: (_snapshot, payload, err) => {
      const outline = payload.openHouse.outline_file
      const file = outline || postingFile(payload.draft)
      if (!file) return
      // An outline file the server refuses is dropped; any other failure keeps the file for a retry.
      const refused = outline && err?.status === 422
      if (refused) {
        setState((prev) => (prev.oh.pendingFile === file ? { ...prev, oh: { ...prev.oh, pendingFile: null } } : prev))
      }
      setUpload((prev) => (prev.file === file ? { status: refused ? "unsupported" : "failed", file } : prev))
    },
    canRetry: () => !explicitSaveRef.current,
  })

  // The draft is created on the first answer, through today's endpoint, then
  // marked Open House. A failed mark retries without minting a second draft.
  const ensureDraft = () => {
    if (stateRef.current.draftId) return Promise.resolve(stateRef.current.draftId)
    if (!creatingRef.current) {
      creatingRef.current = (async () => {
        if (!createdIdRef.current) createdIdRef.current = (await createDraft(createParams(stateRef.current))).id
        const id = createdIdRef.current
        await markOpenHouse(id)
        setStartError(null)
        // Saves queued right after this read the id before the next render.
        stateRef.current = { ...stateRef.current, draftId: id }
        setState((prev) => ({ ...prev, draftId: id }))
        window.history.replaceState({}, "", `/post/open-house/${id}`)
        return id
      })().catch((err) => {
        creatingRef.current = null
        setStartError(err.message)
        throw err
      })
    }
    return creatingRef.current
  }

  const values = useMemo(() => ({ ...state.posting, short_description: state.oh.short_description }), [state.posting, state.oh])
  const steps = service.steps(state.posting, ctx)
  const index = Math.min(state.stepIndex, steps.length - 1)
  const step = steps[index]
  const isReview = step.id === "review"

  // A step change re-anchors the page, or focuses the field a fix jumped to.
  useEffect(() => {
    const key = pendingFocus.current
    if (key) {
      window.requestAnimationFrame(() => {
        focusField(fieldNodes.current, key)
        pendingFocus.current = null
      })
      return
    }
    setShowErr(false)
    topRef.current?.scrollIntoView({ behavior: "instant", block: "start" })
  }, [index])

  // Autosave (debounced), as today's flow does.
  useEffect(() => {
    if (!state.draftId || posted || submitting) return
    const handle = setTimeout(() => saveCurrentDraft().catch(() => {}), 600)
    return () => clearTimeout(handle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.draftId, state.posting, state.oh, state.stepIndex, submitting, posted])

  // Today's /post/price for one writer level; the open range stays client-side.
  const rail = service.rail(state.posting, ctx)
  const priceSignature = JSON.stringify(rail.payload)
  useEffect(() => {
    if (!rail.payload) return
    let cancelled = false
    const handle = setTimeout(async () => {
      const result = await priceProject([rail.payload])
      if (!cancelled) setServerCost({ signature: priceSignature, result })
    }, 400)
    return () => {
      cancelled = true
      clearTimeout(handle)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceSignature])

  const server = serverCost?.signature === priceSignature ? serverCost.result : null
  const railTotal = rail.payload && Number(server?.total) > 0 ? Number(server.totalDiscounted) : rail.total
  const priceRail = (
    <PriceRail lines={rail.lines} total={railTotal} isRange={rail.isRange} fromLabel={rail.fromLabel} note={rail.note} />
  )

  // ── answers ────────────────────────────────────────────────────────────────

  const edit = (updater) => {
    editedRef.current = true
    noteDraftEdit()
    setPostFailed(false)
    setState(updater)
    ensureDraft().catch(() => {})
  }

  const setField = (key, value) => {
    setServerErrors((prev) => (prev[key] ? { ...prev, [key]: null } : prev))
    if (key === "outline_choice" && value === "upgrade") return setField("bundle_tier", PLATINUM)
    // A bundle change (or the outline step's upgrade) never overlaps Post.
    if (key === "bundle_tier" && submitting) return
    if (key === "bundle_tier" && BUNDLE_COMPOSITION[value]?.outline) setUpload({ status: "idle", file: null })
    edit((prev) => answerUpdate(prev, key, value))
  }

  // Upload or paste: saved at once, and the server clears the other source in
  // the same request (the page mirrors it), so only the chosen one is supplied.
  const chooseOutlineSource = (source) => {
    const prevOh = stateRef.current.oh
    if (!source || source === prevOh.outline_source) return
    setServerErrors((prev) => ({ ...prev, outline_choice: null }))
    const changes = source === "upload"
      ? { outline_source: source, outline_text: "" }
      : { outline_source: source, outlineFile: null, pendingFile: null }
    if (source === "paste") setUpload({ status: "idle", file: null })

    // The save below reads the snapshot before the next render.
    stateRef.current = { ...stateRef.current, oh: { ...prevOh, ...changes } }
    edit((prev) => ({ ...prev, oh: { ...prev.oh, ...changes } }))
    ensureDraft().then(() => saveCurrentDraft()).catch(() => {})
  }

  // The outline file saves through the draft save queue like every answer. A
  // saved file it replaces is removed first (persist), and a removal is kept
  // until a save carrying it succeeds, so a refused replacement never brings
  // the old file back.
  const chooseOutlineFile = (file) => {
    setServerErrors((prev) => ({ ...prev, outline_choice: null }))
    if (!isOutlineFile(file)) return setUpload({ status: "unsupported", file })

    const removal = stateRef.current.oh.outlineFile ? ++removalRef.current : stateRef.current.oh.removeFile
    setUpload({ status: "uploading", file })
    edit((prev) => ({ ...prev, oh: { ...prev.oh, pendingFile: file, outlineFile: null, removeFile: removal } }))
  }

  const removeOutline = () => {
    const removal = ++removalRef.current
    setUpload({ status: "idle", file: null })
    edit((prev) => ({ ...prev, oh: { ...prev.oh, outlineFile: null, pendingFile: null, removeFile: removal } }))
  }

  // Editing's manuscript is today's posting file: the File rides the draft save
  // queue and becomes the saved name once the save that sent it succeeds. A
  // removal saves at once, so it is queued ahead of any replacement.
  const chooseManuscript = (file) => {
    setServerErrors((prev) => ({ ...prev, file: null }))
    if (!isManuscriptFile(file)) return setUpload({ status: "unsupported", file })

    setUpload({ status: "uploading", file })
    edit((prev) => ({ ...prev, posting: { ...prev.posting, file } }))
  }

  const removeManuscript = () => {
    setUpload({ status: "idle", file: null })
    stateRef.current = { ...stateRef.current, posting: { ...stateRef.current.posting, file: null } }
    edit((prev) => ({ ...prev, posting: { ...prev.posting, file: null } }))
    ensureDraft().then(() => saveCurrentDraft()).catch(() => {})
  }

  // Waits for the draft to exist and be marked, then for every queued save.
  // Throws (with today's "Draft not saved" banner showing) when either fails.
  const flushSaves = async () => {
    await ensureDraft()
    await saveCurrentDraft({ requireLatest: true })
  }

  // ── navigation ─────────────────────────────────────────────────────────────

  const goTo = (target, focusKey = null) => {
    pendingFocus.current = focusKey
    if (focusKey) setShowErr(true)
    setState((prev) => ({ ...prev, stepIndex: target }))
  }

  const handleContinue = () => {
    const issues = stepIssues(step, values, ctx, state.oh)
    if (issues.length) {
      setShowErr(true)
      window.requestAnimationFrame(() => focusField(fieldNodes.current, issues[0][0]))
      return
    }
    goTo(index + 1)
  }

  const handleSaveExit = async () => {
    if (explicitSaveRef.current) return
    if (state.draftId || editedRef.current) {
      explicitSaveRef.current = true
      try {
        await flushSaves()
      } catch {
        explicitSaveRef.current = false
        return
      }
    }
    router.visit("/projects")
  }

  // ── posting ────────────────────────────────────────────────────────────────

  const handlePost = async () => {
    const blocked = steps.findIndex((s) => stepIssues(s, values, ctx, state.oh).length)
    if (blocked >= 0 && blocked !== index) {
      return goTo(blocked, stepIssues(steps[blocked], values, ctx, state.oh)[0][0])
    }
    if (!terms) {
      setShowErr(true)
      return window.requestAnimationFrame(() => focusField(fieldNodes.current, "terms"))
    }
    if (explicitSaveRef.current) return

    explicitSaveRef.current = true
    setSubmitting(true)
    setPostFailed(false)
    setReviewIssues([])
    try {
      await flushSaves()
    } catch {
      explicitSaveRef.current = false
      setSubmitting(false)
      topRef.current?.scrollIntoView({ behavior: "instant", block: "start" })
      return
    }

    try {
      const result = await submitProject(stateRef.current.draftId)
      if (result?.posted) setPosted(result)
      else router.visit(result?.redirect || "/projects")
    } catch (err) {
      const issues = err?.status === 422 && Array.isArray(err.data?.issues) ? err.data.issues : []
      const targets = issues.map((issue) => ({ issue, target: issueTarget(issue, steps, values, ctx, service.aliases) }))
      const first = targets.find((t) => t.target)
      if (first) {
        setServerErrors(Object.fromEntries(targets.filter((t) => t.target).map((t) => [t.target.key, t.issue.message])))
        setReviewIssues(targets.filter((t) => !t.target).map((t) => t.issue.message))
        goTo(first.target.index, first.target.key)
      } else if (err?.status === 422) {
        setReviewIssues(issues.length ? issues.map((i) => i.message) : err.data?.errors || [err.message])
      } else {
        setPostFailed(true)
      }
    } finally {
      explicitSaveRef.current = false
      setSubmitting(false)
    }
  }

  // ── render ─────────────────────────────────────────────────────────────────

  return (
    <div ref={topRef} className="mx-auto flex max-w-[1040px] flex-col gap-6 px-8 pt-8 pb-14 max-[561px]:px-4">
      <div className="flex items-center gap-3.5 rounded-2xl border border-border bg-card px-5 py-4">
        <span className="grid size-11 shrink-0 place-items-center rounded-xl border border-border bg-secondary">
          <Feather className="size-[21px] text-foreground" strokeWidth={1.8} />
        </span>
        <div className="min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-[0.09em] text-muted-foreground">Post a project</div>
          <h1 className="truncate font-display text-[18px] font-bold tracking-tight text-foreground">
            {(state.posting.title || "").trim() || "New project"}
          </h1>
        </div>
        {state.draftId && savedAt && !saveError && (
          <span className="ml-auto flex shrink-0 items-center gap-1.5 text-[12px] font-semibold text-muted-foreground">
            <Check className="size-3.5 text-done" strokeWidth={2.4} />
            Draft saved
          </span>
        )}
      </div>

      {!posted && (startError || (state.draftId && saveError)) && (
        <div role="alert" className="flex flex-wrap items-center gap-3 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-[13px] text-destructive">
          <span className="min-w-0 flex-1">Draft not saved: {startError || saveError}</span>
          <button
            type="button"
            onClick={() => (state.draftId ? retrySave() : ensureDraft().catch(() => {}))}
            disabled={retryingSave || submitting}
            className="font-semibold underline underline-offset-2 disabled:opacity-50"
          >
            {retryingSave ? "Retrying…" : "Retry save"}
          </button>
        </div>
      )}

      {posted ? (
        <PostedConfirmation
          payload={posted}
          lede={state.posting.tier === options.ALL_TIER_VALUE
            ? `${service.postedBody} ${WORDING.openLevelsPosted}` : service.postedBody}
          hideNextSteps
          onGoToProjects={() => router.visit(posted.projectsPath || "/projects")}
        />
      ) : (
        <WizardShell
          title={step.title}
          sub={step.sub}
          steps={steps.map((s) => ({ id: s.id, title: s.node }))}
          activeIndex={index}
          completedThrough={index - 1}
          stepperCollapseAt={STEPPER_COLLAPSE.service}
          onJump={(i) => goTo(i)}
          onBack={() => goTo(index - 1)}
          backDisabled={index === 0}
          onSaveExit={handleSaveExit}
          onContinue={isReview ? handlePost : handleContinue}
          continueLabel={isReview ? (submitting ? "Posting…" : "Post project") : "Continue"}
          continueDisabled={submitting}
          priceRail={priceRail}
        >
          {isReview ? (
            <ReviewStep
              sections={reviewSections(steps, values, ctx, state.oh)}
              onEdit={(i) => goTo(i)}
              terms={terms}
              onTerms={(v) => {
                setTerms(v)
                setShowErr(false)
              }}
              termsError={showErr && !terms}
              postFailed={postFailed}
              onRetry={handlePost}
              posting={submitting}
              issues={reviewIssues}
            />
          ) : (
            <StepFields
              step={step}
              values={values}
              ctx={ctx}
              oh={state.oh}
              showErr={showErr}
              serverErrors={serverErrors}
              setField={setField}
              fieldNodes={fieldNodes}
              outline={{ upload, onSource: chooseOutlineSource, onFile: chooseOutlineFile, onRetry: retrySave, onRemove: removeOutline }}
              manuscript={{ upload, onFile: chooseManuscript, onRetry: retrySave, onRemove: removeManuscript }}
            />
          )}
        </WizardShell>
      )}
    </div>
  )
}

OpenHousePost.layout = (page) => (
  <AppShell active="post" crumb={["Projects", "Post a Project"]}>
    {page}
  </AppShell>
)
